import { Article } from '../articlesData';

export const articleNemidDigitalSignaturUdfasningMitid: Article = {
  id: 'nemid-digital-signatur-udfasning-mitid',
  slug: 'nemid-digital-signatur-udfasning-mitid',
  title: 'NemID digital signatur: Hvad erstatter NemID, og hvordan underskriver du i dag?',
  subtitle: 'NemID er lukket og slukket. Lær hvad der erstatter NemID til digital signatur, hvordan MitID fungerer til underskrift, og hvad udfasningen betyder for dine aftaler.',
  metaTitle: 'NemID Digital Signatur: Hvad Erstatter NemID i Dag? | KvikSign',
  metaDescription: 'NemID er lukket. Lær hvad der erstatter NemID digital signatur, hvordan MitID fungerer til underskrift af dokumenter, og om dine gamle NemID-signaturer er gyldige.',
  canonicalUrl: 'https://kviksign.com/da/articles/nemid-digital-signatur-udfasning-mitid',
  heroImage: '/images/articles/nemid-digital-signatur-udfasning-mitid.jpg',
  heroImageAlt: 'Overgang fra NemID til MitID med moderne digital identitet og to-faktor sikkerhed',
  category: 'Lovgivning & eIDAS',
  author: {
    name: 'KvikSign',
    role: 'eID-infrastruktur & Historisk e-Signaturanalyse',
    avatar: '/icon.svg'
  },
  publishedAt: '2026-09-12',
  updatedAt: '2026-09-12',
  readingTime: '8 min læsetid',
  primaryKeyword: 'nemid digital signatur',
  secondaryKeywords: [
    'hvad erstatter nemid',
    'nemid underskrift lukket',
    'mitid erstatter nemid',
    'gamle nemid underskrifter gyldighed',
    'mitid erhverv medarbejdersignatur',
    'nemid nøglekort digital signatur'
  ],
  keywords: [
    'nemid digital signatur',
    'hvad erstatter nemid',
    'nemid underskrift lukket',
    'mitid erstatter nemid',
    'gamle nemid underskrifter gyldighed',
    'mitid erhverv medarbejdersignatur',
    'nemid nøglekort digital signatur',
    'digital signatur danmark overblik'
  ],
  keyTakeaways: [
    'NemID blev endegyldigt lukket og erstattet af MitID til borgere og MitID Erhverv til virksomheder.',
    'Det fysiske nøglekort af pap opfyldte ikke længere moderne internationale sikkerhedskrav og var sårbart over for phishing og social engineering.',
    'Dokumenter, der historisk er underskrevet med NemID, bevarer deres fulde juridiske gyldighed, forudsat at PDF\'en indeholder et gyldigt tidsstempel og certifikat (PAdES).',
    'MitID giver markant stærkere beskyttelse mod svindel ved hjælp af app-notifikationer, QR-kodescanning og kontekstuel godkendelse.',
    'På KvikSign er overgangen til MitID fuldendt for længe siden: Du kan oprette, sende og underskrive aftaler med MitID og MitID Erhverv med få klik.'
  ],
  tableOfContents: [
    { id: 'hvorfor-blev-nemid-lukket', title: '1. Hvorfor blev NemID lukket?' },
    { id: 'hvad-erstatter-nemid-i-dag', title: '2. Hvad erstatter NemID til digital signatur i dag?' },
    { id: 'er-gamle-nemid-aftaler-gyldige', title: '3. Er dokumenter underskrevet med NemID stadig juridisk gyldige?' },
    { id: 'de-stoerste-forskelle-for-underskrivere', title: '4. De vigtigste forbedringer: Fra papkort til MitID app' },
    { id: 'mitid-erhverv-og-medarbejdere', title: '5. Hvad med NemID medarbejdersignatur (POCES)?' },
    { id: 'saadan-underskriver-du-i-dag', title: '6. Sådan underskriver du i dag med MitID på KvikSign' },
    { id: 'faq-nemid-udfasning', title: '7. Ofte stillede spørgsmål om NemID og MitID' }
  ],
  sections: [
    {
      id: 'hvorfor-blev-nemid-lukket',
      title: '1. Hvorfor blev NemID lukket?',
      paragraphs: [
        'NemID tjente det danske samfund trofast i over et årti fra lanceringen i 2010. Men den teknologiske udvikling og trusselsbilledet overhalede systemet.',
        'Det berygtede "papkort" med engangskoder var sårbart over for målrettet phishing: Svindlere kunne narre brugere til at udlevere deres brugernavn, adgangskode og en talkode over telefonen eller via falske hjemmesider.',
        'Derudover stillede EU\'s skærpede krav til elektronisk identifikation (eIDAS) og betalingstjenestedirektivet (PSD2) krav om dynamisk kobling mellem godkendelsen og den konkrete handling – noget det statiske NemID-system ikke kunne honorere på tilstrækkelig vis.'
      ]
    },
    {
      id: 'hvad-erstatter-nemid-i-dag',
      title: '2. Hvad erstatter NemID til digital signatur i dag?',
      paragraphs: [
        'I dag er overgangsperioden helt afsluttet, og NemID er erstattet af to separate, men beslægtede systemer:',
        '1. MitID (til privatpersoner): Anvendes til netbank, offentlige selvbetjeningsløsninger (Borger.dk, Sundhed.dk, SKAT) og til digital signering af kontrakter, lejekontrakter, fuldmagter og købsaftaler.',
        '2. MitID Erhverv (til virksomheder og foreninger): Erstatter det gamle NemID medarbejdersignatur. Giver organisationer en central administrationsportal til at tildele rettigheder, administrere medarbejderes tegningsret og logge erhvervsmæssige handlinger separat fra privatsfæren.'
      ]
    },
    {
      id: 'er-gamle-nemid-aftaler-gyldige',
      title: '3. Er dokumenter underskrevet med NemID stadig juridisk gyldige?',
      paragraphs: [
        'Et hyppigt bekymringspunkt hos virksomheder er, om kontrakter, ansættelsesbeviser og generalforsamlingsreferater, der blev underskrevet med NemID tilbage i 2018 eller 2021, mister deres gyldighed, nu hvor NemID er lukket.',
        'Det korte svar er et beroligende: NEJ, de er stadig fuldt gyldige.',
        'Når et dokument er underskrevet digitalt i henhold til PAdES-LTV-standarden (som seriøse e-signatursystemer som KvikSign benytter), indlejres certifikatkæden, rodcertifikaterne og det kryptografiske tidsstempel direkte i PDF-filen på underskriftstidspunktet.',
        'Gyldigheden afhænger af, om signaturen var gyldig på det tidspunkt, den blev afgivet. Dette fremgår af eIDAS-forordningens artikel 32 og 34 om langtidsvalidering.'
      ]
    },
    {
      id: 'de-stoerste-forskelle-for-underskrivere',
      title: '4. De vigtigste forbedringer: Fra papkort til MitID app',
      paragraphs: [
        'For den almindelige underskriver har skiftet til MitID medført markante forbedringer i både sikkerhed og brugervenlighed:'
      ],
      listItems: [
        'Kontekstvisning i appen: Før du godkender, viser MitID-appen præcis, hvad du er ved at godkende (f.eks. "Underskrift af dokument hos KvikSign"), hvilket effektivt eliminerer "blinde" godkendelser.',
        'QR-koder forhindrer mand-i-midten-angreb: Når du underskriver på computeren, scanner du en QR-kode med mobilappen, hvilket sikrer, at mobilen og computeren er i samme fysiske rum.',
        'Hurtigere eksekvering: Intet behov for at lede efter et papkort i pungen – godkendelse klares på få sekunder med biometri (FaceID / fingeraftryk) på telefonen.'
      ]
    },
    {
      id: 'mitid-erhverv-og-medarbejdere',
      title: '5. Hvad med NemID medarbejdersignatur (POCES)?',
      paragraphs: [
        'Tidligere brugte virksomheder NemID medarbejdersignatur (også kendt som POCES-certifikater installeret lokalt i browseren eller som nøglefil). Disse certifikater var berygtede for installationsbøvl, Java-konflikter og manglende mobilitet.',
        'MitID Erhverv har revolutioneret dette. I dag kan medarbejdere enten:',
        'A) Benytte deres eksisterende MitID-app til at godkende arbejdsrelaterede signaturer – uden at private data deles med arbejdsgiveren.',
        'B) Benytte en separat MitID Erhverv app, hvis virksomhedens sikkerhedspolitik kræver streng fysisk adskillelse på en arbejdsudleveret telefon.'
      ]
    },
    {
      id: 'saadan-underskriver-du-i-dag',
      title: '6. Sådan underskriver du i dag med MitID på KvikSign',
      paragraphs: [
        'På KvikSign er hele overgangen gennemført for længst. Både private og erhvervsdrivende kan sende og modtage dokumenter til underskrift med MitID uden tekniske forhindringer:',
        '1. Opret en gratis konto hos KvikSign og modtag 5 gratis credits.',
        '2. Upload din PDF-kontrakt.',
        '3. Angiv modtagerens e-mail og eventuelle mobilnummer.',
        '4. Modtageren underskriver på sekunder med MitID på sin telefon eller computer.',
        '5. Begge parter modtager straks det underskrevne dokument forseglet med eIDAS-kompatibelt certifikat.'
      ]
    },
    {
      id: 'faq-nemid-udfasning',
      title: '7. Ofte stillede spørgsmål om NemID og MitID',
      paragraphs: [
        'Her er de mest almindelige spørgsmål om overgangen fra NemID til MitID for digital underskrift.'
      ]
    }
  ],
  faqs: [
    {
      question: 'Kan man stadig underskrive dokumenter med NemID?',
      answer: 'Nej. NemID-serverne og NemID-certifikatudstederen (DanID / Nets) er permanent lukket ned for transaktioner. Alle digitale signaturer i Danmark foregår i dag via MitID eller MitID Erhverv.'
    },
    {
      question: 'Er mine gamle NemID-underskrevne kontrakter stadig bindende?',
      answer: 'Ja. Aftaleloven og eIDAS-forordningen sikrer, at en gyldig signatur afgivet i NemID-æraen forbliver fuldt juridisk bindende i hele aftalens løbetid.'
    },
    {
      question: 'Kræver MitID-signering en app?',
      answer: 'De fleste bruger MitID-appen på smartphone eller tablet, men personer uden smartphone kan anvende en MitID-kodeviser eller MitID-kodeoplæser til digital signering.'
    },
    {
      question: 'Hvad er forskellen på MitID og MitID Erhverv ved underskrifter?',
      answer: 'Begge verificerer underskriverens identitet med højeste sikkerhedsniveau. MitID Erhverv knytter derudover medarbejderens signatur direkte til virksomhedens CVR-nummer og organisationsstruktur.'
    }
  ],
  sources: [
    {
      title: 'Digitaliseringsstyrelsen – Udfasning af NemID og overgang til MitID',
      url: 'https://digst.dk/'
    },
    {
      title: 'MitID.dk – Officiel informationsportal for MitID',
      url: 'https://www.mitid.dk/'
    },
    {
      title: 'Retsinformation – Lov om MitID og NemLog-in',
      url: 'https://www.retsinformation.dk/'
    }
  ],
  disclaimer: 'Denne artikel er udarbejdet til historisk og juridisk orientering om overgangen fra NemID til MitID og udgør ikke individuel juridisk rådgivning.'
};
